import { useState } from "react";
import interviewAuthService from "../services/interviewAuthService";

export function useQuestionnaireEngine({ interviewId }) {
  //  DATA-ONLY state (single source of truth)
  const [questionnaireQueue, setQuestionnaireQueue] = useState([]);
  const [usedQuestionIds, setUsedQuestionIds] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

  const CACHE_KEY = `questionnaire_cache_${interviewId}`;
  const USED_QUESTIONS_KEY = `used_questions_${interviewId}`;

  const fetchQuestionsFromAPI = async (excludeIds = []) => {
    const companyId = sessionStorage.getItem("companyId") || "";
    setIsLoadingQuestions(true);

    try {
      let response;

      if (excludeIds.length > 0) {
        response = await interviewAuthService.makeAuthenticatedRequest(
          `${process.env.REACT_APP_API_OCR_BASE_URL}/get-mcq-questions-filtered/?interview_id=${interviewId}&company_id=${companyId}`,
          {
            method: "POST",
            body: JSON.stringify(excludeIds),
          },
        );
      } else {
        response = await interviewAuthService.makeAuthenticatedRequest(
          `${process.env.REACT_APP_API_OCR_BASE_URL}/get-mcq-questions/?interview_id=${interviewId}&company_id=${companyId}`,
          { method: "GET" },
        );
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.mcq_questions || [];
    } catch (error) {
      console.error("Error fetching questions:", error);
      return [];
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const convertApiQuestionToComponentFormat = (apiQuestion) => {
    let questionType = "single-choice";
    let options = [];
    let leftOptions = [];
    let rightOptions = [];
    let correctMatches = {};

    switch (apiQuestion.type_id) {
      case 1:
        questionType = "multiple-choice";
        options = Object.values(apiQuestion.options || {});
        break;
      case 2:
        questionType = "true-false";
        options = ["True", "False"];
        break;
      case 3:
        questionType = "match-following";
        if (apiQuestion.options) {
          const values = Object.values(apiQuestion.options);
          const mid = Math.ceil(values.length / 2);
          leftOptions = values.slice(0, mid);
          rightOptions = values.slice(mid);

          rightOptions.forEach((_, i) => {
            correctMatches[i] = leftOptions[i] || leftOptions[0];
          });
        }
        break;
      default:
        options = Object.values(apiQuestion.options || {});
    }

    return {
      id: apiQuestion.id,
      type: questionType,
      title: apiQuestion.question,
      difficulty: apiQuestion.difficulty,
      type_id: apiQuestion.type_id,
      options,
      leftOptions,
      rightOptions,
      correctMatches,
      originalOptions: apiQuestion.options,
    };
  };

  const loadQuestionsToCache = async () => {
    const cached = sessionStorage.getItem(CACHE_KEY);
    const usedIds = JSON.parse(
      sessionStorage.getItem(USED_QUESTIONS_KEY) || "[]",
    );

    if (!cached || JSON.parse(cached).length === 0) {
      const apiQuestions = await fetchQuestionsFromAPI(usedIds);

      if (apiQuestions.length > 0) {
        const converted = apiQuestions.map(convertApiQuestionToComponentFormat);
        setQuestionnaireQueue(converted);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(converted));
      }
    }
  };

  const getNextQuestion = async () => {
    let queue = [...questionnaireQueue];

    if (queue.length === 0) {
      const usedIds = JSON.parse(
        sessionStorage.getItem(USED_QUESTIONS_KEY) || "[]",
      );
      const apiQuestions = await fetchQuestionsFromAPI(usedIds);

      if (apiQuestions.length === 0) return null;

      queue = apiQuestions.map(convertApiQuestionToComponentFormat);
    }

    const next = queue[0];
    const remaining = queue.slice(1);

    setQuestionnaireQueue(remaining);
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(remaining));

    return next;
  };

  const markQuestionAsUsed = (questionId) => {
    const currentUsed = JSON.parse(
      sessionStorage.getItem(USED_QUESTIONS_KEY) || "[]",
    );

    if (!currentUsed.includes(questionId)) {
      const updated = [...currentUsed, questionId];
      setUsedQuestionIds(updated);
      sessionStorage.setItem(USED_QUESTIONS_KEY, JSON.stringify(updated));
    }
  };

  const submitAnswerToAPI = async (
    question,
    selectedAnswers,
    draggedItems,
    speedToAnswer,
    maxRetries = 3,
    initialDelayMs = 500,
  ) => {
    const companyId = sessionStorage.getItem("companyId") || "";
    const storedInterviewId = sessionStorage.getItem("interviewId");

    if (!storedInterviewId) return false;

    let answer = "";

    switch (question.type_id) {
      case 1:
        answer = selectedAnswers?.map((i) => question.options[i]).join(",");
        break;
      case 2:
        answer = selectedAnswers?.[0] === 0 ? "True" : "False";
        break;
      case 3:
        if (draggedItems) {
          answer = `${draggedItems.leftItems.join(",")}:${draggedItems.rightItems.join(",")}`;
        }
        break;
      default:
        answer = question.options?.[selectedAnswers?.[0]] || "";
    }

    const payload = {
      interview_id: storedInterviewId,
      question_id: question.id,
      answer,
      speed_to_answer: speedToAnswer,
      company_id: companyId,
    };

    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await interviewAuthService.makeAuthenticatedRequest(
          `${process.env.REACT_APP_API_OCR_BASE_URL}/set-mcq-answers/`,
          {
            method: "POST",
            body: JSON.stringify(payload),
          },
        );

        if (response.ok) {
          if (attempt > 0) {
            console.log(`Answer submitted successfully on retry ${attempt}`);
          }
          return true;
        }

        // Do NOT retry on client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          const errorText = await response.text();
          console.error(
            `Client error (${response.status}) - not retrying: ${errorText}`,
          );
          return false;
        }

        // Server error (5xx) or network issue - retry
        const errorText = await response.text();
        lastError = new Error(
          `HTTP ${response.status}: ${errorText || response.statusText}`,
        );
        console.warn(
          `Attempt ${attempt + 1}/${maxRetries + 1} failed: ${lastError.message}`,
        );
      } catch (error) {
        // Network error - retry
        lastError = error;
        console.warn(
          `Attempt ${attempt + 1}/${maxRetries + 1} failed (network): ${error.message}`,
        );
      }

      // If this was the last attempt, give up
      if (attempt === maxRetries) {
        console.error(
          `Failed to submit answer after ${maxRetries + 1} attempts`,
          lastError,
        );
        return false;
      }

      // Exponential backoff: delay = initialDelayMs * 2^attempt
      const delayMs = initialDelayMs * Math.pow(2, attempt);
      console.log(`Retrying in ${delayMs}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    return false;
  };

  return {
    loadQuestionsToCache,
    getNextQuestion,
    markQuestionAsUsed,
    submitAnswerToAPI,
    isLoadingQuestions,
  };
}
